import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { AssetTypesService } from 'app/_services/asset-types.service';
import { NotificationService } from 'app/_services/notification.service';
import { SiteAssetsService } from 'app/_services/site-assets.service';
import { SiteService } from 'app/_services/site.service';
import { WorkOrderRequiredCompetencyService } from 'app/_services/work-order-required-competency.service';

const TURBINE_CATEGORY_ID = 3;

@Component({
  selector: 'app-add-edit-asset',
  templateUrl: './add-edit-asset.component.html'
})
export class AddEditAssetComponent implements OnInit {
  @ViewChild('assetImageInput') assetImageInput: ElementRef;

  private siteId: any;
  public assetUnderEditId: any;

  public actionType: any;
  public typeTitle: any;

  private routeRole: any;

  public assetForm: FormGroup;
  public allAssetCategories: any[] = [];
  public manufacturerOptions: any[] = [];
  public modelOptions: any[] = [];
  public ratingOptions: any[] = [];

  public selectedAssetImageArray = [];

  public saveInProgress = false;
  public areFilesAdded = false;

  public addTabItems = [];
  public editTabItems = [];

  public loadingAssetCategories = true;
  public loadingEditAsset = true;
  public loadingTurbineOptions = false;
  public isTurbineAsset = false;

  public exemptFromAssetScheduling: boolean;
  public allowAssetScheduling: boolean;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private siteService: SiteService,
    private siteAssetService: SiteAssetsService,
    private assetTypesService: AssetTypesService,
    private turbineReferenceService: WorkOrderRequiredCompetencyService,
    private notificationService: NotificationService,
    globals: Globals,
    fb: FormBuilder,
  ) {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.siteId = params.id;
      this.actionType = params.type;
      this.assetUnderEditId = params.assetId;

      if (this.actionType === 'edit') {
        this.typeTitle = 'Edit Asset';
      } else {
        this.typeTitle = 'Add Asset';
        this.loadingEditAsset = false;
      }
    });
    this.routeRole = globals.user.routeRole;

    this.assetForm = fb.group({
      name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
      description: new FormControl('', [Validators.required, Validators.maxLength(255)]),
      manufacturer: new FormControl('', [Validators.maxLength(255)]),
      model: new FormControl('', [Validators.maxLength(255)]),
      rating: new FormControl('', [Validators.maxLength(50)]),
      latitude: new FormControl(null, [Validators.min(-90), Validators.max(90)]),
      longitude: new FormControl(null, [Validators.min(-180), Validators.max(180)]),
      assetCategories: new FormControl([], [Validators.required]),
      exemptFromAssetScheduling: new FormControl()
      // image field not a formControl
    });
  }

  ngOnInit() {
    this.setupTurbineFieldSubscriptions();
    this.resetTurbineFields();
    this.setupAssetCategories(this.siteId);

    if (this.actionType === 'edit') {
      this.siteAssetService.getAssetById(this.assetUnderEditId).subscribe((data: any) => {
        const selectedAsset = data;
        const existingAssetCategories = selectedAsset.assetCategories.map(cat => {
          return {
            label: cat.name,
            value: cat.id
          };
        });
        this.typeTitle = selectedAsset.site.name + ' - ' + selectedAsset.name;
        this.allowAssetScheduling = selectedAsset.site.siteOwner.siteOwnerUser.allowAssetScheduling;

        this.assetForm.patchValue({
          name: selectedAsset.name,
          description: selectedAsset.description,
          manufacturer: selectedAsset.manufacturer,
          model: selectedAsset.model,
          rating: selectedAsset.rating,
          latitude: selectedAsset.latitude !== undefined && selectedAsset.latitude !== null ? selectedAsset.latitude : null,
          longitude: selectedAsset.longitude !== undefined && selectedAsset.longitude !== null ? selectedAsset.longitude : null,
          assetCategories: existingAssetCategories,
          exemptFromAssetScheduling: selectedAsset.exemptFromAssetScheduling
        }, { emitEvent: false });

        this.isTurbineAsset = this.hasTurbineCategory(existingAssetCategories);
        this.syncTurbineFields();
        this.loadingEditAsset = false;

      });

      this.editTabItems = [
        { path: '/' + this.routeRole + '/sites/' + this.siteId + '/assets/' + this.assetUnderEditId, title: 'Back to Asset', class: 'back-link' },
        { path: '/' + this.routeRole + '/sites/' + this.siteId + '/assets/indi-asset', pathParams: { type: 'edit', assetId: this.assetUnderEditId }, title: 'Edit Details' },
      ];
    }

    this.addTabItems = [
      { path: '/' + this.routeRole + '/sites/' + this.siteId + '/assets/', title: 'Back to Assets', class: 'back-link' },
      { path: '/' + this.routeRole + '/sites/' + this.siteId + '/assets/indi-asset', pathParams: { type: 'add' }, title: 'Add Asset' },
    ];
  }

  private setupAssetCategories(siteid) {
    this.siteService.getSiteById(siteid).subscribe((data: any) => {
      this.getAllAssetCategoriesForSiteType(data.siteType.id);


      if (this.actionType === 'add') {
        this.typeTitle = data.name + ' - Add Asset';
      }
    });
  }

  private setupTurbineFieldSubscriptions(): void {
    this.assetForm.get('assetCategories').valueChanges.subscribe((selectedCategories) => {
      this.isTurbineAsset = this.hasTurbineCategory(selectedCategories);
      this.syncTurbineFields();
    });

    this.assetForm.get('manufacturer').valueChanges.subscribe((manufacturer) => {
      this.onManufacturerChange(manufacturer);
    });
  }

  private getAllAssetCategoriesForSiteType(siteTypeId) {
    this.assetTypesService.getAllAssetTypesForSiteType(siteTypeId).subscribe((categoryList: any) => {
      if (!categoryList.length) {
        this.notificationService.notify('danger', 'Error', 'Could not load Categories');
      }

      const arr = [];
      categoryList.forEach(element => {
        arr.push({
          label: element.name,
          value: element.id
        });
      });
      this.allAssetCategories = arr;
      this.loadingAssetCategories = false;
    }, (error => {
      console.error(error);
      this.notificationService.notify('danger', 'Error', 'Could not load Categories');
      this.loadingAssetCategories = false;
    }));
  }

  // Handle Image Selection onFileChange
  public onFileChange(event) {
    const file = event.target.files[0];

    if (!file) {
      this.selectedAssetImageArray = [];
      this.areFilesAdded = false;
      return;
    }

    this.selectedAssetImageArray = [file]; // just one image
    this.areFilesAdded = true;
  }

  public viewSelectedAssetImage() {
    if (this.selectedAssetImageArray.length) {
      const fileUrl = URL.createObjectURL(this.selectedAssetImageArray[0]);
      window.open(fileUrl, '_blank');
      window.setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
    }
  }

  public removeSelectedAssetImage() {
    this.selectedAssetImageArray = [];
    this.areFilesAdded = false;

    if (this.assetImageInput && this.assetImageInput.nativeElement) {
      this.assetImageInput.nativeElement.value = '';
    }
  }

  public onCoordinateKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    const allowedKeys = ['Backspace', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End'];
    if (allowedKeys.indexOf(event.key) !== -1 || /^[0-9.]$/.test(event.key)) {
      return;
    }

    if (event.key === '-') {
      const input = event.target as HTMLInputElement;
      const selectionStart = input.selectionStart || 0;
      const selectionEnd = input.selectionEnd || 0;
      const minusIndex = input.value.indexOf('-');
      const isReplacingMinus = minusIndex >= selectionStart && minusIndex < selectionEnd;

      if (selectionStart === 0 && (minusIndex === -1 || isReplacingMinus)) {
        return;
      }
    }

    event.preventDefault();
  }

  public onAssetFormSubmit() {
    if (!this.assetForm.valid || (this.actionType === 'add' && !this.areFilesAdded) || (this.isTurbineAsset && this.loadingTurbineOptions)) {
      return;
    }

    this.saveInProgress = true;
    const values = this.assetForm.getRawValue();
    const manufacturer = this.isTurbineAsset ? this.getOptionText(values.manufacturer) : '';
    const model = this.isTurbineAsset ? this.getOptionText(values.model) : '';
    const rating = this.isTurbineAsset ? this.getOptionText(values.rating) : '';
    const latitude = this.toNumberOrNull(values.latitude);
    const longitude = this.toNumberOrNull(values.longitude);
    const assetCategoryIds = values.assetCategories.map(category => this.toOptionValue(category));

    if (this.actionType === 'add') {
      const body = {
        name: values.name,
        description: values.description,
        manufacturer,
        model,
        rating,
        latitude,
        longitude,
        assetCategoryIds,
        siteId: this.siteId,
        imageArray: this.selectedAssetImageArray,
      };

      this.siteAssetService.addAssetToSite(body).subscribe((data: any) => {
        this.notificationService.notify('success', 'Successfully added new Asset');
        this.goBackToAssets();
      }, (error: any) => {
        this.saveInProgress = false;
        throw error;
      });
    } else {
      const body = {
        name: values.name,
        description: values.description,
        manufacturer,
        model,
        rating,
        latitude,
        longitude,
        assetCategoryIds,
        id: this.assetUnderEditId,
        exemptFromAssetScheduling: values.exemptFromAssetScheduling,
      };

      this.siteAssetService.editAsset(body).subscribe((data: any) => {
        this.notificationService.notify('success', 'Successfully saved Asset changes');
        this.goBackToAssetPage();
      }, (error: any) => {
        this.saveInProgress = false;
        throw error;
      });
    }
  }

  private goBackToAssets() {
    this.router.navigate(['/' + this.routeRole + '/sites/' + this.siteId + '/assets']);
  }

  private goBackToAssetPage() {
    this.router.navigate(['/' + this.routeRole + '/sites/' + this.siteId + '/assets/' + this.assetUnderEditId]);
  }

  private hasTurbineCategory(selectedCategories: any[]): boolean {
    return selectedCategories.some(category => Number(this.toOptionValue(category)) === TURBINE_CATEGORY_ID);
  }

  private syncTurbineFields(): void {
    if (!this.isTurbineAsset) {
      this.resetTurbineFields();
      return;
    }

    this.assetForm.get('manufacturer').enable({ emitEvent: false });

    if (this.manufacturerOptions.length === 1 && !this.loadingTurbineOptions) {
      this.loadTurbineManufacturers();
      return;
    }

    if (this.assetForm.get('manufacturer').value) {
      this.loadTurbineModelsAndRatings(this.assetForm.get('manufacturer').value);
      return;
    }

    this.resetModelAndRatingFields();
  }

  private resetTurbineFields(): void {
    this.assetForm.patchValue({
      manufacturer: null,
      model: null,
      rating: null,
    }, { emitEvent: false });

    this.manufacturerOptions = [this.createOptionalDropdownOption()];
    this.resetModelAndRatingFields();
    this.assetForm.get('manufacturer').disable({ emitEvent: false });
  }

  private resetModelAndRatingFields(): void {
    this.modelOptions = [this.createOptionalDropdownOption()];
    this.ratingOptions = [this.createOptionalDropdownOption()];
    this.assetForm.patchValue({
      model: null,
      rating: null,
    }, { emitEvent: false });
    this.assetForm.get('model').disable({ emitEvent: false });
    this.assetForm.get('rating').disable({ emitEvent: false });
  }

  private loadTurbineManufacturers(): void {
    if (this.loadingTurbineOptions) {
      return;
    }

    this.loadingTurbineOptions = true;
    this.assetForm.get('manufacturer').disable({ emitEvent: false });

    this.turbineReferenceService.getTurbineManufacturers().subscribe((manufacturers: any) => {
      const manufacturerNames: string[] = Array.from(new Set<string>(manufacturers))
        .sort((a: string, b: string) => a.localeCompare(b));

      this.manufacturerOptions = [this.createOptionalDropdownOption(), ...manufacturerNames.map(manufacturer => ({ label: manufacturer, value: manufacturer }))];
      this.loadingTurbineOptions = false;
      if (this.isTurbineAsset) {
        this.assetForm.get('manufacturer').enable({ emitEvent: false });

        if (this.assetForm.get('manufacturer').value) {
          this.loadTurbineModelsAndRatings(this.assetForm.get('manufacturer').value);
        }
      }
    }, () => {
      this.loadingTurbineOptions = false;
      this.notificationService.notify('danger', 'Error', 'Could not load Manufacturer list');
      if (this.isTurbineAsset) {
        this.assetForm.get('manufacturer').enable({ emitEvent: false });
      }
    });
  }

  private onManufacturerChange(manufacturer: string): void {
    if (!this.isTurbineAsset) {
      return;
    }

    this.resetModelAndRatingFields();

    if (!manufacturer) {
      return;
    }

    this.loadTurbineModelsAndRatings(manufacturer);
  }

  private loadTurbineModelsAndRatings(manufacturer: string): void {
    this.loadingTurbineOptions = true;
    this.assetForm.get('model').disable({ emitEvent: false });
    this.assetForm.get('rating').disable({ emitEvent: false });

    this.turbineReferenceService.searchTurbines({ manufacturer }).subscribe((items: any) => {
      const modelNames: string[] = items
        .map((item: any) => item.model)
        .filter((model: string) => !!model)
        .map((item: string) => String(item));
      const ratingNames: string[] = items
        .map((item: any) => item.rating)
        .filter((rating: string) => !!rating)
        .map((item: any) => String(item));
      const models: string[] = Array.from(new Set<string>(modelNames))
        .sort((a: string, b: string) => a.localeCompare(b));
      const ratings: string[] = Array.from(new Set<string>(ratingNames))
        .sort((a: string, b: string) => a.localeCompare(b));

      this.loadingTurbineOptions = false;
      if (!this.isTurbineAsset || this.assetForm.get('manufacturer').value !== manufacturer) {
        return;
      }

      this.modelOptions = [this.createOptionalDropdownOption(), ...models.map(model => ({ label: model, value: model }))];
      this.ratingOptions = [this.createOptionalDropdownOption(), ...ratings.map(rating => ({ label: rating, value: rating }))];
      this.assetForm.get('model').enable({ emitEvent: false });
      this.assetForm.get('rating').enable({ emitEvent: false });
    }, () => {
      this.loadingTurbineOptions = false;
      this.notificationService.notify('danger', 'Error', 'Could not load turbine model and rating options');
      this.assetForm.get('model').enable({ emitEvent: false });
      this.assetForm.get('rating').enable({ emitEvent: false });
    });
  }

  private toNumberOrNull(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private toOptionValue(option: any): any {
    return option && option.value !== undefined ? option.value : option;
  }

  private getOptionText(option: any): string {
    const value = this.toOptionValue(option);
    return value === null || value === undefined ? '' : value;
  }

  private createOptionalDropdownOption() {
    return { label: 'Optional', value: null };
  }

}
